/* eslint-disable react/display-name */
import {getCumulativeOffset} from '@utils/index';
import useCommon from '@utils/hooks/common';
import classNames from 'classnames';
import React, {forwardRef, useContext, useImperativeHandle, useRef, useState} from 'react';
import {AbsolutePosition} from '@interface/index';
import {CHIP_FADING_DURATION, CHIP_MOVING_DURATION, CHIP_TRANSITION_DURATION} from '@constant/game';
import styles from './styles';
import {PositionContext} from '@utils/context';
import {motion, AnimatePresence} from 'framer-motion';
import {useDebugView} from '@utils/hooks/debug';

interface MovableObjectInterface {
  children: JSX.Element;
  destination?: AbsolutePosition;
  // eslint-disable-next-line react/no-unused-prop-types
  containerElement?: HTMLElement;
}

export const RESET_DELAY = 500;
const HIDDEN_DELAY = 1500;

const MovableObject = forwardRef((props: MovableObjectInterface, ref) => {
  // eslint-disable-next-line react/prop-types
  const {classes, theme} = useCommon({withStyles: styles});
  const [leftOffset, setLeftOffset] = useState<number | string>(0);
  const [topOffset, setTopOffset] = useState<number | string>(0);
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isHidden, setIsHidden] = useState<boolean>(false);

  const isDebugView = useDebugView();

  const selfRef = useRef<HTMLElement>();
  const {children, destination} = props;

  const {scaleUpValue} = useContext(PositionContext);

  const moveBy = (left: number, top: number) => {
    setLeftOffset(prev => Number(prev) + left);
    setTopOffset(prev => Number(prev) + top);
  };

  const moveTo = (leftDest: number, topDest: number) => {
    const currentAbsolutePosition = getCumulativeOffset(selfRef.current);

    // Move the object's center to destination. So clientWidth and height are factored in
    // transform: scale() affects getBoundingClientRect() result
    const extraLeft = currentAbsolutePosition.width / 2 || 0;
    const extraTop = currentAbsolutePosition.height / 2 || 0;

    const leftDiff = (leftDest - extraLeft - currentAbsolutePosition.left) / scaleUpValue;
    const topDiff = (topDest - extraTop - currentAbsolutePosition.top) / scaleUpValue;

    unhide();
    moveBy(leftDiff, topDiff);
    setIsMoving(true);
  };

  const unhide = () => {
    setIsHidden(false);
    setIsFadingOut(false);
  };

  const resetPosition = () => {
    setLeftOffset(0);
    setTopOffset(0);
  };

  const variants = {
    initial: {
      opacity: 1,
      x: 0,
      y: 0,
    },
    moving: {
      opacity: 1,
      x: leftOffset,
      y: topOffset,
      transition: {
        duration: CHIP_MOVING_DURATION / 1000,
        // ease: [0.25, 1, 0.5, 1],
      },
    },
    fadingOut: {
      opacity: 0,
      transition: {duration: CHIP_FADING_DURATION / 1000},
      x: leftOffset,
      y: topOffset,
    },
    hidden: {
      // ...
    },
  };

  const getAnimationState = () => {
    if (isFadingOut) return 'fadingOut';
    if (isMoving) return 'moving';
    return 'initial';
  };

  useImperativeHandle(
    ref,
    () => ({
      moveToPot: () => {
        // console.log(' @@ moveToPot');
        moveTo(destination.left, destination.top);
        // ...
        setTimeout(() => {
          setIsMoving(false);
          setIsFadingOut(true);
        }, CHIP_FADING_DURATION);
        setTimeout(() => {
          setIsHidden(true);
        }, CHIP_TRANSITION_DURATION);
        // * not sure whats the code below for
        setTimeout(() => {
          resetPosition();
        }, CHIP_TRANSITION_DURATION + RESET_DELAY);
        setTimeout(() => {
          setIsHidden(false);
        }, CHIP_TRANSITION_DURATION + HIDDEN_DELAY);
      },
      moveToWithoutReset: (dest: AbsolutePosition) => {
        moveTo(dest.left, dest.top);
      },
      moveToDestinationWithFade: (dest: AbsolutePosition) => {
        // console.log(' ## moveToDestination');
        moveTo(dest.left, dest.top);

        setTimeout(() => {
          setIsMoving(false);
          setIsFadingOut(true);
        }, CHIP_FADING_DURATION);
        setTimeout(() => {
          setIsHidden(true);
        }, CHIP_TRANSITION_DURATION);
        setTimeout(() => {
          resetPosition();
        }, CHIP_TRANSITION_DURATION + RESET_DELAY);
        // setTimeout(() => {
        //   unhide();
        // }, CHIP_TRANSITION_DURATION + HIDDEN_DELAY);
      },
      containerElement: selfRef?.current,
      unhide,
      hide: () => {
        setIsHidden(true);
      },
      resetPosition,
      getAbsolutePosition: (): AbsolutePosition => {
        if (!selfRef?.current) return null;
        return getCumulativeOffset(selfRef.current);
      },
    }),
    [destination, selfRef, moveTo]
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <motion.article
      ref={selfRef}
      className={classNames(classes.movableContainer, {
        [classes.isHidden]: isHidden,
      })}
      variants={variants}
      style={{background: isDebugView ? 'rgba(200, 0, 0, 0.3)' : 'transparent'}}
      animate={getAnimationState()}
      onAnimationComplete={definition => {
        // if (definition === 'moving') {
        //   setFadingOut(true);
        // }
      }}
    >
      {children}
    </motion.article>
  );
});

export default MovableObject;

MovableObject.displayName = 'MovableObject';
